
import { useState, useRef, useEffect } from 'react';
import { Package, Star, Video, ArrowRight, ArrowLeft, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

export type CaseStudyType = {
  id: string;
  title: string;
  description: string;
  result: string;
  icon: React.ElementType;
  color: string;
  image: string;
  image_detail: string;
  slug: string;
  brandLogo?: string;
  brandName?: string;
  goal?: string;
  keyMetric?: string;
};

export const caseStudies: CaseStudyType[] = [
  {
    id: '1',
    title: "D2C Brand Growth with Barter Collabs",
    description: "Boosting Reach & Sales with Cost-Efficient Barter Influencer Strategy.",
    result: "50K+ reach, 5K+ engagement, 28% increase in website visits",
    icon: Package,
    color: "bg-woost-blue",
    image: "/images/bombay.png",
    image_detail: "/images/bscbanner.png",
    slug: "d2c-brand-growth",
    brandLogo: "/images/bombay.png",
    brandName: "Bombay Shaving Company",
    goal: "Boost awareness and engagement for a new launch with budget-friendly influencer collabs.",
    keyMetric: "1.1 M+ reach"
  },
  {
    id: '2',
    title: "Enhancing Marketplace Performance & Visibility",
    description: "Driving Performance with People-Led Content.",
    result: "Average rating improved from 3.5 to 4.7 stars, 150+ quality reviews",
    icon: Star,
    color: "bg-woost-yellow",
    image: "/images/indeekos.png",
    image_detail: "/images/indeekosbanner.png",
    slug: "marketplace-rating-boost",
    brandLogo: "/images/indeekos.png",
    brandName: "Indeekos",
    goal: "Strengthen marketplace visibility and credibility ",
    keyMetric: "Boosted trust, visibility, and conversions."
  },
  {
    id: '3',
    title: "UGC Video Campaigns for Brands",
    description: "Driving Engagement and Visibility with Real User Video Content.",
    result: "2.8x ROAS, 45% decrease in CPA, 25+ unique videos delivered",
    icon: Video,
    color: "bg-teal-500",
    image: "/images/ugc 1.jpg",
    image_detail: "/images/ugcbanner.png",
    slug: "ugc-ad-campaign",
    brandLogo: "",
    brandName: "",
    goal: "Drive brand awareness and engagement through authentic UGC content.",
    keyMetric: "2.8x ROAS"
  },
  {
    id: '4',
    title: "Driving D2C Growth with Targeted Paid Collab",
    description: "Driving Brand Growth Through Influencer Content.",
    result: "125% increase in direct sales, 15,000+ authentic UGC posts",
    icon: Package,
    color: "bg-woost-purple",
    image: "/images/agaro.png",
    image_detail: "/images/agarobanner.png",
    slug: "nano-influencer-community",
    brandLogo: "/images/agaro.png",
    brandName: "Agaro",
    goal: "Scale visibility and trust through strategic paid influencer collaborations.",
    keyMetric: "2.3x traffic growth, 38% rise in conversions"
  }
];

const CaseStudyCard = ({ caseStudy }: { caseStudy: CaseStudyType }) => {
  const Icon = caseStudy.icon;
  
  return (
    <motion.div 
      className="rounded-xl shadow-lg overflow-hidden h-full flex flex-col bg-white"
      whileHover={{ 
        y: -10,
        transition: { duration: 0.3 }
      }}
    >
      <div className="relative h-48">
        <img 
          src={caseStudy.image} 
          alt={caseStudy.title} 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
          <div className="p-6">
            <div className="flex items-center gap-2 mb-2">
              {caseStudy.brandLogo && (
                <img src={caseStudy.brandLogo} alt={caseStudy.brandName} className="h-6 rounded-full bg-white p-1" />
              )}
              <span className="text-white text-sm font-medium">{caseStudy.brandName}</span>
            </div>
            <h3 className="text-white text-xl font-bold">{caseStudy.title}</h3>
          </div>
        </div>
        
        <div className={`absolute top-4 right-4 ${caseStudy.color} p-2 rounded-lg`}>
          <Icon size={20} className="text-white" />
        </div>
      </div>
      
      <div className="p-6 flex-grow flex flex-col">
        <div className="mb-4">
          <div className="text-sm text-gray-500 mb-1">Goal:</div>
          <div className="font-medium">{caseStudy.goal}</div>
        </div>
        
        <div className="mb-6">
          <div className="text-sm text-gray-500 mb-1">Key Result:</div>
          <div className="font-bold text-xl text-gray-800">{caseStudy.keyMetric}</div>
        </div>
        
        <div className="mt-auto">
          <Link to={`/case-study/${caseStudy.slug}`}>
            <Button 
              variant="outline" 
              className="w-full border-woost-blue text-woost-blue hover:bg-woost-blue hover:text-white"
            >
              See How It Worked <ExternalLink size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

const CaseStudiesSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6
      }
    }
  };
  
  return (
    <section id="case-studies" className="py-20 bg-gray-50" ref={sectionRef}>
      <div className="container mx-auto px-4 md:px-6">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Success Stories</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Real results from brands that leverage Woost's influencer marketing solutions
          </p>
        </motion.div>
        
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {caseStudies.map((caseStudy) => (
            <motion.div key={caseStudy.id} variants={itemVariants}>
              <CaseStudyCard caseStudy={caseStudy} />
            </motion.div>
          ))}
        </motion.div>
        
        {/* <motion.div 
          className="text-center mt-12"
          initial={{ opacity: 0 }}
          animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <Button 
            className="bg-white border-2 border-woost-blue text-woost-blue hover:bg-woost-blue hover:text-white"
          >
            View All Case Studies <ArrowRight size={16} className="ml-2" />
          </Button>
        </motion.div> */}
      </div>
    </section>
  );
};

export default CaseStudiesSection;
